import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { TablePagination } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import useAuthFetch from "../../hooks/useAuthFetch";

const PER_PAGE = 10;

/**
 * The journey's identity-verification call log: one row per face-verification
 * API call that actually answered, written by the recorder during the journey.
 *
 * These are the rows the Overall Verification Score is averaged from, so the
 * point of showing them is that a reviewer can check the score rather than take
 * it on trust — which is why the raw response body is reachable per row.
 *
 * Fetched on mount, which is the same thing as "when the panel opens": the MUI
 * Drawer wrapping JourneySidePanel unmounts its children on close, so every
 * open mounts this component fresh. Nothing loads while the panel is shut.
 */
export default function JourneyIdentityLogs({ journeyId }) {
  const customFetch = useAuthFetch();

  const [rows, setRows] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [page, setPage] = useState(0); // 0-indexed for MUI; the API is 1-indexed
  const [isLoading, setIsLoading] = useState(true);
  // Distinct from "no rows": a reviewer validating a score must be able to tell
  // "this journey made no identity calls" from "we could not load them".
  const [hasError, setHasError] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchLogs = useCallback(
    async (requestedPage) => {
      if (!journeyId) return;

      setIsLoading(true);
      setHasError(false);

      try {
        const url = new URL(
          `${process.env.REACT_APP_API_URL}/v1/dashboard/log/fetchAll`
        );
        url.search = new URLSearchParams({
          journeyId,
          logType: "IDENTITY",
          page: requestedPage + 1,
          perPage: PER_PAGE,
        }).toString();

        const { response, status } = await customFetch(url, "GET");

        if (status === 200) {
          setRows(response?.data || []);
          setPageInfo(response?.pageInfo || null);
        } else {
          setRows([]);
          setPageInfo(null);
          setHasError(true);
        }
      } catch (error) {
        console.error("Failed to fetch identity logs:", error);
        setRows([]);
        setPageInfo(null);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [journeyId]
  );

  useEffect(() => {
    fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId, page]);

  const handleChangePage = (_event, newPage) => {
    setExpandedId(null);
    setPage(newPage);
  };

  const toggleRow = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="mt-4 border-2 border-gray-300 flex flex-col">
      <p className="text-base border-b-2 px-3 py-2.5 bg-gray-100 border-gray-300 font-bold text-black-150 mb-0">
        Identity Verification Calls
      </p>

      {isLoading ? (
        <div className="w-full py-3 px-3">
          <div className="h-14 w-full bg-gray-300 rounded relative overflow-hidden animate-pulse" />
        </div>
      ) : hasError ? (
        <div className="flex items-center justify-center gap-3 py-3 px-3 text-base">
          <span>Failed to load identity logs.</span>
          <button
            type="button"
            onClick={() => fetchLogs(page)}
            className="text-blue-600 underline"
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex items-center justify-center py-3 px-3 text-base">
          No Data Available
        </div>
      ) : (
        <>
          <div className="flex flex-col">
            <div className="flex border-b-2 border-gray-300 bg-gray-50">
              <p className="text-sm font-semibold text-black-150 px-3 py-2 mb-0 w-48 shrink-0">
                Time
              </p>
              <p className="text-sm font-semibold text-black-150 px-3 py-2 mb-0 flex-1">
                Message
              </p>
            </div>

            {rows.map((row, index) => {
              // Sequelize serialises by ATTRIBUTE name, so the primary key
              // arrives as `id` even though its column is "ID" — while
              // CREATED_AT really is the attribute name, because that rename
              // happens in the model's options block rather than via `field`.
              // Fall back to the index so a missing/renamed key can never make
              // every row share one identity and expand together.
              const rowKey = row.id ?? `row-${index}`;
              const isExpanded = expandedId === rowKey;
              return (
                <div key={rowKey} className="border-b border-gray-300">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleRow(rowKey)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") toggleRow(rowKey);
                    }}
                    className="flex items-start cursor-pointer hover:bg-gray-50"
                  >
                    <p className="text-sm text-black-150 px-3 py-2 mb-0 w-48 shrink-0">
                      {row.CREATED_AT
                        ? format(new Date(row.CREATED_AT), "dd MMM yyyy, hh:mm:ss a")
                        : "-"}
                    </p>
                    <p className="text-sm text-black-150 px-3 py-2 mb-0 flex-1 flex items-start gap-2">
                      {isExpanded ? (
                        <KeyboardArrowDown fontSize="small" className="mt-0.5 shrink-0" />
                      ) : (
                        <KeyboardArrowRight fontSize="small" className="mt-0.5 shrink-0" />
                      )}
                      <span>{row.logMessage}</span>
                    </p>
                  </div>

                  {isExpanded && (
                    <pre className="text-xs bg-gray-50 border-t border-gray-200 px-3 py-2 mb-0 overflow-x-auto">
                      {formatMetadata(row.metadata)}
                    </pre>
                  )}
                </div>
              );
            })}
          </div>

          <TablePagination
            component="div"
            count={pageInfo?.total || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={PER_PAGE}
            rowsPerPageOptions={[PER_PAGE]}
          />
        </>
      )}
    </div>
  );
}

/**
 * METADATA holds the API's response body verbatim. It arrives as an object on
 * most dialects and as the stored JSON string on others, so handle both rather
 * than rendering "[object Object]" or a wall of unformatted JSON.
 */
function formatMetadata(metadata) {
  if (!metadata) return "No response body recorded.";
  try {
    const parsed = typeof metadata === "string" ? JSON.parse(metadata) : metadata;
    return JSON.stringify(parsed, null, 2);
  } catch (error) {
    return String(metadata);
  }
}
